// src/features/dashboard/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import UserTopNav from "../../../components/layout/UserTopNav";
import { gymConfig } from "../../../config/gymConfig";
import {
  Flame,
  Dumbbell,
  PersonStanding,
  Award,
  Activity,
  CalendarDays,
  ChevronRight,
  Zap,
  Timer,
  Share,
  Sun,
  Moon,
  ArrowUpRight,
  Clock3,
} from "lucide-react";
import SessionShareModal from "../../wod/components/SessionShareModal";

// ── Types ────────────────────────────────────────────────────────────────────

type BookingDoc = {
  classId: string;
  userId: string;
  status: "booked" | "cancelled";
  createdAt?: Timestamp;
};

type ClassDoc = {
  title: string;
  timezone?: string;
  startTime: Timestamp;
  endTime: Timestamp;
  coachName?: string;
  location?: string;
  capacity?: number;
  bookedCount?: number;
  status?: "scheduled" | "cancelled";
};

type ClassWithId = { id: string; data: ClassDoc };

type UserStats = {
  totalCheckIns?: number;
  monthCheckIns?: Record<string, number>;
  currentStreak?: number;
  longestStreak?: number;
};

type TrainingLog = {
  movementSlug: string;
  metricType: string;
  value: string;
  unit: string;
  date: string;
};

type PBs = {
  backSquat: string | null;
  flatBench: string | null;
  run1km: string | null;
};

type SessionKey = "AM" | "PM";

type SessionSharePayload = {
  title: string;
  subtitle?: string;
  filename: string;
  shareTitle: string;
  shareText: string;
  dateLabel: string;
  sessionLabel: string;
  sessionTimeLabel?: string;
  sessionType: string;
  sessionStyle: string;
  sessionExtra?: string;
  highlight: string;
  highlightLabel: string;
  stationsLabel: string;
  coachNote?: string;
  items: string[];
};

type DashboardSessionCard = {
  key: SessionKey;
  timeLabel: string;
  title: string;
  meta: string;
  detail: string;
  payload: SessionSharePayload | null;
};

type Movement = {
  id?: string;
  name: string;
  target?: string;
  notes?: string;
};

type Station = {
  id?: string;
  title: string;
  movements: Movement[];
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const TZ = "Europe/London";

function greeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: TZ }).format(
      new Date()
    )
  );
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 18) return "Good afternoon";
  return "Good evening";
}

function todayKeyLondon(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());
}

function monthKeyLondon(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value ?? "0000";
  const m = parts.find((p) => p.type === "month")?.value ?? "00";
  return `${y}-${m}`;
}

function datekeyLondon(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(d);
}

function fmtTime(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TZ,
  }).format(d);
}

function fmtDayShort(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: TZ,
  }).format(d);
}

function fmtTodayFull(): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: TZ,
  }).format(new Date());
}

function parseSeconds(value: string): number | null {
  const v = value.trim();
  if (v.includes(":")) {
    const [min, sec] = v.split(":").map(Number);
    if (Number.isFinite(min) && Number.isFinite(sec)) return min * 60 + sec;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatSeconds(total: number): string {
  const s = Math.max(0, Math.round(total));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function parseDurationToSeconds(duration: unknown): number | null {
  if (!duration || typeof duration !== "string") return null;
  const value = duration.trim();
  if (!value) return null;

  const parts = value.split(":").map((part) => part.trim());
  if (parts.length === 2) {
    const mm = Number(parts[0]);
    const ss = Number(parts[1]);
    if (Number.isFinite(mm) && Number.isFinite(ss)) return mm * 60 + ss;
  }

  const mins = value.match(/(\d+)\s*m/i);
  const secs = value.match(/(\d+)\s*s/i);
  const total = (mins ? Number(mins[1]) * 60 : 0) + (secs ? Number(secs[1]) : 0);
  return total > 0 ? total : null;
}

function normalizeMovements(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((movement) => {
      if (typeof movement === "string") return movement;
      if (movement && typeof movement === "object") {
        const value = (movement as { partner1?: string; movement?: string }).partner1
          ?? (movement as { movement?: string }).movement
          ?? "";
        return String(value);
      }
      return String(movement ?? "");
    })
    .map((value) => value.trim())
    .filter(Boolean);
}

function normalizeStations(rawStations: unknown, rawMovements: unknown): Station[] {
  if (Array.isArray(rawStations) && rawStations.length) {
    return rawStations.map((station, index) => {
      const s = station as {
        id?: string;
        title?: string;
        movements?: Array<{ id?: string; name?: string; target?: string; notes?: string }>;
      };

      return {
        id: s.id,
        title: String(s.title ?? `Station ${index + 1}`).trim() || `Station ${index + 1}`,
        movements: (Array.isArray(s.movements) ? s.movements : [])
          .map((movement) => ({
            id: movement.id,
            name: String(movement.name ?? "").trim(),
            target: String(movement.target ?? "").trim() || undefined,
            notes: String(movement.notes ?? "").trim() || undefined,
          }))
          .filter((movement) => movement.name || movement.target || movement.notes),
      };
    });
  }

  const legacy = normalizeMovements(rawMovements);
  if (legacy.length) {
    return [
      {
        title: "Station 1",
        movements: legacy.map((name) => ({ name })),
      },
    ];
  }

  return [];
}

function getSessionTimeLabel(sessionKey: SessionKey) {
  return sessionKey === "AM" ? "6AM" : "6PM";
}

function getDashboardSessionPayload(
  wod: any,
  sessionKey: SessionKey,
  dateKey: string
): SessionSharePayload | null {
  if (!wod) return null;

  const selectedDate = new Date(dateKey);
  if (Number.isNaN(selectedDate.getTime())) return null;

  const dayName = selectedDate.toLocaleDateString("en-GB", { weekday: "long" });
  const dateLabel = selectedDate.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionKey);

  if (wod.sessionType === "Strength") {
    const movements = Array.isArray(wod.strengthMovements) ? wod.strengthMovements : [];
    const items = movements
      .map((movement: any) => {
        const name = String(movement?.movement ?? "").trim();
        const percent = String(movement?.percent ?? "").trim();
        const repRange = String(movement?.repRange ?? "").trim();
        const details = [percent, repRange].filter(Boolean).join(" • ");
        return name ? `${name}${details ? ` • ${details}` : ""}` : "";
      })
      .filter(Boolean);

    return {
      title: wod.wodName?.trim() || "Strength",
      subtitle: `${dayName} strength session`,
      filename: `${dateKey}-${sessionKey.toLowerCase()}-session.png`,
      shareTitle: `${sessionTimeLabel} session`,
      shareText: `Today's ${sessionTimeLabel.toLowerCase()} session is live: ${wod.wodName?.trim() || "Strength session"}`,
      dateLabel,
      sessionLabel: sessionKey,
      sessionTimeLabel,
      sessionType: "STRENGTH",
      sessionStyle: "STRENGTH",
      sessionExtra: `${movements.length || 0} STATIONS`,
      highlight: `${movements.length || 0}`,
      highlightLabel: "Stations",
      stationsLabel: `${movements.length || 0} strength stations`,
      coachNote: String(wod?.strengthCue ?? "").trim() || undefined,
      items,
    };
  }

  const stations = normalizeStations(wod?.stations, wod?.movements);
  const stationCount = stations.length;
  const timerMode = wod?.timerMode === "stationControlled" ? "stationControlled" : "timed";
  const groupSize = typeof wod?.groupSize === "number" && wod.groupSize > 0 ? wod.groupSize : null;
  const roundsRaw = wod?.rounds;
  const rounds =
    typeof roundsRaw === "number" && roundsRaw >= 1
      ? roundsRaw
      : typeof roundsRaw === "string" && roundsRaw.trim() && !Number.isNaN(Number(roundsRaw))
      ? Math.max(1, Math.floor(Number(roundsRaw)))
      : null;
  const roundDurationSeconds =
    typeof wod?.roundDurationSeconds === "number" && wod.roundDurationSeconds > 0
      ? wod.roundDurationSeconds
      : parseDurationToSeconds(wod?.duration);
  const controlStationIndex =
    typeof wod?.controlStationIndex === "number" && wod.controlStationIndex >= 0
      ? wod.controlStationIndex
      : null;
  const controlStationTitle =
    controlStationIndex != null ? stations[controlStationIndex]?.title ?? null : null;

  const items = stations
    .map((station, index) => {
      const title = (station.title || `Station ${index + 1}`).trim();
      const movementNames = station.movements
        .map((movement) => String(movement.name ?? "").trim())
        .filter(Boolean)
        .slice(0, 2)
        .join(" + ");
      return movementNames ? `${title} • ${movementNames}` : title;
    })
    .filter(Boolean);

  const highlight =
    timerMode === "timed"
      ? roundDurationSeconds && rounds
        ? `${formatSeconds(roundDurationSeconds)} x ${rounds}`
        : `${stationCount || 0}`
      : controlStationIndex != null
      ? `${controlStationIndex + 1}/${stationCount || 1}`
      : `${stationCount || 0}`;

  const highlightLabel =
    timerMode === "timed"
      ? "Timer"
      : controlStationIndex != null
      ? "Control"
      : "Stations";

  const sessionExtra =
    wod.sessionType === "HYROX"
      ? `${groupSize ? `GROUP OF ${groupSize}` : "GROUP"} | ${timerMode === "timed" ? "TIMED" : "STATION CONTROL"}`
      : "—";

  const stationsLabel =
    timerMode === "timed"
      ? `${stationCount || 0} stations • ${rounds || 1} rounds`
      : controlStationTitle
      ? `Control station: ${controlStationTitle}`
      : `${stationCount || 0} stations`;

  return {
    title: wod.wodName?.trim() || `${dayName} Session`,
    subtitle: `${dayName} HYROX session`,
    filename: `${dateKey}-${sessionKey.toLowerCase()}-session.png`,
    shareTitle: `${sessionTimeLabel} session`,
    shareText: `Today's ${sessionTimeLabel.toLowerCase()} session is live: ${wod.wodName?.trim() || "HYROX session"}`,
    dateLabel,
    sessionLabel: sessionKey,
    sessionTimeLabel,
    sessionType: "HYROX",
    sessionStyle: String(wod.wodType ?? "HYROX").toUpperCase(),
    sessionExtra,
    highlight,
    highlightLabel,
    stationsLabel,
    items,
  };
}

function typeMeta(title?: string) {
  const t = (title || "").toLowerCase();
  if (t.includes("hyrox"))
    return {
      label: "HYROX",
      stripe: "bg-yellow-500/80",
      icon: <Flame className="h-4 w-4" />,
      pill: "border-orange-500/30 bg-orange-500/10 text-orange-200",
    };
  if (t.includes("strength"))
    return {
      label: "STRENGTH",
      stripe: "bg-sky-500/80",
      icon: <Dumbbell className="h-4 w-4" />,
      pill: "border-sky-500/30 bg-sky-500/10 text-sky-200",
    };
  if (t.includes("bags"))
    return {
      label: "BAGS",
      stripe: "bg-red-500/80",
      icon: <Award className="h-4 w-4" />,
      pill: "border-red-500/30 bg-red-500/10 text-red-200",
    };
  if (t.includes("yoga"))
    return {
      label: "YOGA",
      stripe: "bg-teal-500/80",
      icon: <PersonStanding className="h-4 w-4" />,
      pill: "border-teal-500/30 bg-teal-500/10 text-teal-200",
    };
  if (t.includes("run club"))
    return {
      label: "RUN CLUB",
      stripe: "bg-orange-500/80",
      icon: <Activity className="h-4 w-4" />,
      pill: "border-orange-500/30 bg-orange-500/10 text-orange-200",
    };
  return {
    label: (title || "SESSION").toUpperCase(),
    stripe: "bg-white/20",
    icon: <Zap className="h-4 w-4" />,
    pill: "border-white/15 bg-white/5 text-white/80",
  };
}

function buildLockerRoomHeadline(gymCount: number) {
  if (gymCount <= 1) return "Welcome to the Locker Room.";
  return `You're in ${gymCount} gyms' Locker Room.`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { appUser } = useAuth();
  const auth = getAuth();

  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [pbs, setPbs] = useState<PBs>({ backSquat: null, flatBench: null, run1km: null });
  const [bookedClasses, setBookedClasses] = useState<ClassWithId[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [todayProgramming, setTodayProgramming] = useState<Record<string, any> | null>(null);
  const [sharePayload, setSharePayload] = useState<SessionSharePayload | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Track auth user
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, [auth]);

  // Fetch user stats once
  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "users", user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data() as { stats?: UserStats };
        setStats(data.stats ?? null);
      }
    });
  }, [user]);

  // Fetch PBs from trainingLogs subcollection
  useEffect(() => {
    if (!user) return;

    const logsRef = collection(db, "users", user.uid, "trainingLogs");

    async function fetchBest(slug: string, metricType: string, lowerIsBetter = false) {
      const snap = await getDocs(
        query(logsRef, where("movementSlug", "==", slug), where("metricType", "==", metricType))
      );
      let best: number | null = null;
      snap.docs.forEach((d) => {
        const log = d.data() as TrainingLog;
        const parsed = parseSeconds(log.value);
        if (parsed === null) return;
        if (best === null) { best = parsed; return; }
        if (lowerIsBetter ? parsed < best : parsed > best) best = parsed;
      });
      return best;
    }

    Promise.all([
      fetchBest("back-squat", "1RM"),
      fetchBest("flat-bench", "1RM"),
      fetchBest("1km-run", "Time Trial", true),
    ]).then(([backSquat, flatBench, run1km]) => {
      setPbs({
        backSquat: backSquat !== null ? `${backSquat} kg` : null,
        flatBench: flatBench !== null ? `${flatBench} kg` : null,
        run1km: run1km !== null ? formatSeconds(run1km) : null,
      });
    }).catch((e) => console.error("PB fetch error:", e));
  }, [user]);

  // Subscribe to active bookings, then fetch corresponding class docs
  useEffect(() => {
    if (!user) {
      setBookedClasses([]);
      setLoadingClasses(false);
      return;
    }

    const q = query(
      collection(db, "bookings"),
      where("userId", "==", user.uid),
      where("status", "==", "booked")
    );

    const unsub = onSnapshot(q, async (snap) => {
      const classIds = snap.docs.map((d) => (d.data() as BookingDoc).classId).filter(Boolean);

      if (classIds.length === 0) {
        setBookedClasses([]);
        setLoadingClasses(false);
        return;
      }

      // Firestore "in" supports up to 30 per query
      const chunks: string[][] = [];
      for (let i = 0; i < classIds.length; i += 30) {
        chunks.push(classIds.slice(i, i + 30));
      }

      try {
        const results: ClassWithId[] = [];
        await Promise.all(
          chunks.map(async (chunk) => {
            const { documentId } = await import("firebase/firestore");
            const classSnap = await getDocs(
              query(collection(db, "classes"), where(documentId(), "in", chunk))
            );
            classSnap.docs.forEach((d) => {
              results.push({ id: d.id, data: d.data() as ClassDoc });
            });
          })
        );

        // Sort by startTime ascending, filter future / today only
        const now = Date.now();
        const upcoming = results
          .filter((c) => c.data.startTime.toDate().getTime() > now - 60 * 60 * 1000) // within last hour
          .sort((a, b) => a.data.startTime.toMillis() - b.data.startTime.toMillis());

        setBookedClasses(upcoming);
      } catch (e) {
        console.error("Dashboard class fetch error:", e);
      } finally {
        setLoadingClasses(false);
      }
    });

    return () => unsub();
  }, [user]);

  useEffect(() => {
    const todayRef = doc(db, "wods", todayKeyLondon());
    return onSnapshot(
      todayRef,
      (snap) => setTodayProgramming(snap.exists() ? (snap.data() as Record<string, any>) : null),
      (error) => console.error("Dashboard programming fetch error:", error)
    );
  }, []);

  const firstName = appUser?.name?.split(" ")[0] || appUser?.email?.split("@")[0] || "there";

  const todayKey = todayKeyLondon();
  const todayClasses = bookedClasses.filter(
    (c) => datekeyLondon(c.data.startTime.toDate()) === todayKey
  );
  const upcomingClasses = bookedClasses.filter(
    (c) => datekeyLondon(c.data.startTime.toDate()) !== todayKey
  );

  const thisMonthCount = stats?.monthCheckIns?.[monthKeyLondon()] ?? 0;
  const streak = stats?.currentStreak ?? 0;
  const total = stats?.totalCheckIns ?? 0;
  const sessionCards: DashboardSessionCard[] = (["AM", "PM"] as SessionKey[]).map((key) => {
    const payload = getDashboardSessionPayload(todayProgramming?.[key], key, todayKey);
    const emptyLabel = key === "AM" ? "6AM" : "6PM";

    if (!payload) {
      return {
        key,
        timeLabel: emptyLabel,
        title: "Coming soon",
        meta: "Programming not published yet",
        detail: "Check back later for today’s session.",
        payload: null,
      };
    }

    return {
      key,
      timeLabel: payload.sessionTimeLabel || emptyLabel,
      title: payload.title,
      meta: `${payload.sessionType} · ${payload.sessionStyle}`,
      detail: payload.stationsLabel,
      payload,
    };
  });
  const nextClass =
    bookedClasses.find((item) => item.data.startTime.toDate().getTime() >= Date.now()) ?? null;
  const headline = buildLockerRoomHeadline(1);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <UserTopNav />

      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-6 sm:py-8">
        <section className="relative overflow-hidden rounded-[30px] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.20),transparent_32%),linear-gradient(180deg,rgba(10,12,20,0.98),rgba(6,7,12,0.99))] px-5 py-6 shadow-[0_28px_90px_rgba(3,6,18,0.46)] sm:px-7 sm:py-7">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(142,163,255,0.40)] to-transparent" />

          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/48">
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-white/54">
                {fmtTodayFull()}
              </span>
              <span className="rounded-full border border-[color:rgba(136,156,255,0.18)] bg-[color:rgba(59,84,170,0.18)] px-3 py-1.5 text-[color:#dde5ff]">
                {greeting()} {firstName}
              </span>
            </div>

            <div className="mt-5">
              <h1 className="mt-3 max-w-4xl font-heading text-[2.65rem] uppercase leading-[0.9] tracking-[-0.065em] text-white sm:text-[4.2rem]">
                {headline}
              </h1>
            </div>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <div className="rounded-[22px] border border-[color:rgba(136,156,255,0.10)] bg-[linear-gradient(180deg,rgba(17,21,34,0.88),rgba(9,11,18,0.82))] p-4">
              <div className="flex items-center gap-2 text-[color:#aabaff]">
                <Clock3 className="h-4 w-4" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Next up</span>
              </div>
              <div className="mt-3 text-lg font-heading uppercase tracking-[-0.04em] text-white">
                {nextClass ? typeMeta(nextClass.data.title).label : "No booking yet"}
              </div>
              <p className="mt-1 text-sm text-white/56">
                {nextClass
                  ? `${fmtDayShort(nextClass.data.startTime.toDate())} · ${fmtTime(nextClass.data.startTime.toDate())}${nextClass.data.location ? ` · ${nextClass.data.location}` : ""}`
                  : "Open the schedule and lock in your next session."}
              </p>
            </div>

            <div className="rounded-[22px] border border-[color:rgba(136,156,255,0.10)] bg-[linear-gradient(180deg,rgba(17,21,34,0.88),rgba(9,11,18,0.82))] p-4">
              <div className="flex items-center gap-2 text-[color:#aabaff]">
                <Activity className="h-4 w-4" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Pulse</span>
              </div>
              <div className="mt-3 flex items-end gap-2">
                <span className="bg-gradient-to-b from-white to-[rgba(223,231,255,0.82)] bg-clip-text font-heading text-3xl tracking-[-0.08em] text-transparent">
                  {todayClasses.length}
                </span>
                <span className="pb-1 text-xs uppercase tracking-[0.14em] text-white/32">
                  booked today
                </span>
              </div>
              <p className="mt-1 text-sm text-white/56">
                {sessionCards.filter((session) => session.payload).length}/2 sessions live. {streak} {streak === 1 ? "week" : "weeks"} on the streak.
              </p>
            </div>

            <Link
              to="/schedule"
              className="flex min-h-[152px] flex-col justify-between rounded-[22px] border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(23,29,47,0.94),rgba(12,15,23,0.88))] p-4 transition hover:bg-[linear-gradient(180deg,rgba(28,35,56,0.98),rgba(14,18,28,0.92))]"
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[color:#aabaff]">
                Open schedule
              </div>
              <div>
                <div className="mt-6 text-lg font-heading uppercase tracking-[-0.04em] text-white">
                  {thisMonthCount} check-ins
                </div>
                <p className="mt-1 text-sm text-white/56">
                  {total} total visits recorded in your Locker Room history.
                </p>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[color:#e0e7ff]">
                View full schedule
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </Link>
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.95fr)]">
          <div className="rounded-[28px] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.12),transparent_28%),linear-gradient(180deg,rgba(13,15,23,0.98),rgba(8,9,14,0.98))] p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:#a4b6ff]">
                  Today in Motion
                </p>
                <h2 className="mt-2 text-2xl font-heading uppercase tracking-[-0.05em] text-white">
                  Your booked sessions
                </h2>
              </div>
              <Link
                to="/schedule"
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/64 transition hover:text-white"
              >
                Schedule
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-6">
              {loadingClasses ? (
                <div className="border-t border-[color:rgba(136,156,255,0.10)] pt-4 text-sm text-white/45">Loading your sessions…</div>
              ) : todayClasses.length === 0 ? (
                <div className="border-t border-[color:rgba(136,156,255,0.10)] pt-4">
                  <p className="text-base text-white/68">Nothing booked for today yet.</p>
                  <p className="mt-1 text-sm text-white/44">
                    Open the schedule and build your day inside {gymConfig.brand.shortName}.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[rgba(136,156,255,0.06)] border-t border-[color:rgba(136,156,255,0.10)]">
                  {todayClasses.map(({ id, data }) => {
                    const meta = typeMeta(data.title);
                    const start = data.startTime.toDate();
                    const end = data.endTime.toDate();

                    return (
                      <div key={id} className="grid gap-3 py-4 sm:grid-cols-[112px_minmax(0,1fr)_auto] sm:items-center">
                        <div className="text-sm font-semibold uppercase tracking-[0.16em] text-white/42">
                          {fmtTime(start)}<span className="text-white/24">–{fmtTime(end)}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${meta.pill}`}>
                              {meta.icon}
                              {meta.label}
                            </span>
                            {data.location ? (
                              <span className="text-xs uppercase tracking-[0.18em] text-white/34">
                                {data.location}
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-2 text-base text-white/80">
                            {data.coachName ? `${data.coachName} is leading this one.` : "You’re booked in and ready to go."}
                          </p>
                        </div>
                        <span className="inline-flex h-fit items-center rounded-full border border-emerald-500/35 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
                          Booked
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[28px] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_right,rgba(58,84,168,0.12),transparent_26%),linear-gradient(180deg,rgba(14,16,24,0.94),rgba(9,10,15,0.9))] p-5 sm:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:#a4b6ff]">
              Programming Board
            </p>
            <h2 className="mt-2 text-2xl font-heading uppercase tracking-[-0.05em] text-white">
              Today&apos;s sessions
            </h2>

            <div className="mt-6 space-y-3">
              {sessionCards.map((session) => {
                const Icon = session.key === "AM" ? Sun : Moon;

                return (
                  <div
                    key={session.key}
                    className="rounded-[22px] border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(13,16,27,0.78),rgba(8,9,14,0.56))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(136,156,255,0.14)] bg-[color:rgba(59,84,170,0.12)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:#dde5ff]">
                          <Icon className="h-3 w-3" />
                          {session.timeLabel}
                        </div>
                        <div className="mt-3 text-xl font-heading uppercase tracking-[-0.04em] text-white">
                          {session.title}
                        </div>
                        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/42">
                          {session.meta}
                        </div>
                      </div>
                      {session.payload ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSharePayload(session.payload);
                            setShareOpen(true);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(136,156,255,0.14)] bg-[color:rgba(59,84,170,0.12)] px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:#dde5ff] transition hover:bg-[color:rgba(59,84,170,0.18)]"
                        >
                          <Share className="h-3 w-3" />
                          Share
                        </button>
                      ) : null}
                    </div>

                    <div className="mt-4 border-t border-white/8 pt-3 text-sm leading-6 text-white/64">
                      {session.detail}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="rounded-[28px] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.10),transparent_24%),linear-gradient(180deg,rgba(15,16,24,0.94),rgba(10,11,15,0.9))] p-5 sm:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:#a4b6ff]">
              Performance
            </p>
            <h2 className="mt-2 text-2xl font-heading uppercase tracking-[-0.05em] text-white">
              Your numbers
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {[
                { label: "Streak", value: streak, suffix: streak === 1 ? "week" : "weeks", icon: <Flame className="h-5 w-5 text-orange-400" /> },
                { label: "This month", value: thisMonthCount, suffix: thisMonthCount === 1 ? "class" : "classes", icon: <CalendarDays className="h-5 w-5 text-sky-400" /> },
                { label: "All time", value: total, suffix: total === 1 ? "check-in" : "check-ins", icon: <Award className="h-5 w-5 text-emerald-400" /> },
              ].map(({ label, value, suffix, icon }) => (
                <div key={label} className="border-t border-[color:rgba(136,156,255,0.10)] pt-4">
                  <div className="flex items-center gap-2 text-white/58">{icon}</div>
                  <div className="mt-3 text-4xl font-heading tracking-[-0.08em] text-white">{value}</div>
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/42">
                    {label}
                  </div>
                  <div className="mt-1 text-xs text-white/34">{suffix}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[28px] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_right,rgba(58,84,168,0.10),transparent_24%),linear-gradient(180deg,rgba(15,16,24,0.94),rgba(10,11,15,0.9))] p-5 sm:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:#a4b6ff]">
              Personal Bests
            </p>
            <h2 className="mt-2 text-2xl font-heading uppercase tracking-[-0.05em] text-white">
              Benchmarks to beat
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {([
                { label: "Back Squat", sub: "1RM", value: pbs.backSquat, icon: <Dumbbell className="h-5 w-5 text-sky-400" />, link: "/training/strength/back-squat" },
                { label: "Flat Bench", sub: "1RM", value: pbs.flatBench, icon: <Dumbbell className="h-5 w-5 text-violet-400" />, link: "/training/strength/flat-bench" },
                { label: "1km Run", sub: "Time Trial", value: pbs.run1km, icon: <Timer className="h-5 w-5 text-orange-400" />, link: "/training/engine/1km-run" },
              ] as const).map(({ label, sub, value, icon, link }) => (
                <Link
                  key={label}
                  to={link}
                  className="border-t border-[color:rgba(136,156,255,0.10)] pt-4 transition hover:text-white"
                >
                  <div className="flex items-center gap-2 text-white/58">{icon}</div>
                  <div className="mt-3 text-3xl font-heading tracking-[-0.08em] text-white">
                    {value ?? "—"}
                  </div>
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/42">
                    {label}
                  </div>
                  <div className="mt-1 text-xs text-white/34">{sub}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Upcoming Bookings ────────────────────────────────── */}
        {upcomingClasses.length > 0 && (
          <section className="rounded-[28px] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.08),transparent_26%),linear-gradient(180deg,rgba(15,16,24,0.94),rgba(10,11,15,0.9))] p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:#a4b6ff]">
                  Looking Ahead
                </p>
                <h2 className="mt-2 text-2xl font-heading uppercase tracking-[-0.05em] text-white">
                  Upcoming bookings
                </h2>
              </div>
              <Link
                to="/schedule"
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/64 transition hover:text-white"
              >
                Manage
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-6 overflow-hidden rounded-[22px] border border-[color:rgba(136,156,255,0.10)] divide-y divide-[rgba(136,156,255,0.06)]">
              {upcomingClasses.slice(0, 5).map(({ id, data }) => {
                const meta = typeMeta(data.title);
                const start = data.startTime.toDate();
                const end = data.endTime.toDate();
                return (
                  <div key={id} className="flex items-center gap-4 bg-[linear-gradient(180deg,rgba(13,15,23,0.42),rgba(8,9,13,0.26))] px-5 py-4">
                    <div className={`shrink-0 w-1 self-stretch rounded-full ${meta.stripe}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-heading tracking-widest text-white">{meta.label}</div>
                      <div className="mt-0.5 text-xs text-white/40">
                        {fmtDayShort(start)} · {fmtTime(start)}–{fmtTime(end)}
                        {data.coachName ? ` · ${data.coachName}` : ""}
                        {data.location ? ` · ${data.location}` : ""}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 border ${meta.pill}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
      {sharePayload ? (
        <SessionShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          title={sharePayload.title}
          subtitle={sharePayload.subtitle}
          filename={sharePayload.filename}
          shareTitle={sharePayload.shareTitle}
          shareText={sharePayload.shareText}
          dateLabel={sharePayload.dateLabel}
          sessionLabel={sharePayload.sessionLabel}
          sessionTimeLabel={sharePayload.sessionTimeLabel}
          sessionType={sharePayload.sessionType}
          sessionStyle={sharePayload.sessionStyle}
          sessionExtra={sharePayload.sessionExtra}
          highlight={sharePayload.highlight}
          highlightLabel={sharePayload.highlightLabel}
          stationsLabel={sharePayload.stationsLabel}
          coachNote={sharePayload.coachNote}
          items={sharePayload.items}
        />
      ) : null}
    </div>
  );
}
